import React, { useState, useEffect } from 'react';
import { Users, Plus, Search } from 'lucide-react';
import axiosInstance from '../../../config/axios';
import ClientCard from './ClientCard';
import ClientFormModal from './ClientFormModal';

interface Client {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  contact_count?: number;
  is_active: boolean;
  created_at: string;
}

interface ClientFormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  website: string;
  notes: string;
}

const initialFormData: ClientFormData = {
  name: '',
  company: '',
  email: '',
  phone: '',
  address: '',
  website: '',
  notes: ''
};

export default function ClientsManagement() {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchQuery]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    if (!searchQuery) {
      setFilteredClients(clients);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
    setFilteredClients(filtered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingClient) {
        await axiosInstance.put(`/clients/${editingClient.id}`, formData);
      } else {
        await axiosInstance.post('/clients', formData);
      }

      await loadClients();
      handleCloseForm();
    } catch (error) {
      console.error('Failed to save client:', error);
      alert('Failed to save client');
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      company: client.company || '',
      email: client.email || '',
      phone: client.phone || '',
      address: '',
      website: client.website || '',
      notes: ''
    });
    setShowFormModal(true);
  };

  const handleDelete = async (clientId: number) => {
    if (confirm('Are you sure you want to delete this client? This will also remove all associated contacts.')) {
      try {
        await axiosInstance.delete(`/clients/${clientId}`);
        await loadClients();
      } catch (error) {
        console.error('Failed to delete client:', error);
        alert('Failed to delete client');
      }
    }
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditingClient(null);
    setFormData(initialFormData);
  };

  const handleOpenAddForm = () => {
    setEditingClient(null);
    setFormData(initialFormData);
    setShowFormModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1">Manage your client organizations and contacts</p>
        </div>
        <button
          onClick={handleOpenAddForm}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Client
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No clients found</p>
            <button
              onClick={handleOpenAddForm}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              Add your first client
            </button>
          </div>
        ) : (
          filteredClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Add/Edit Client Modal */}
      <ClientFormModal
        isOpen={showFormModal}
        editingClient={editingClient}
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleSubmit}
        onClose={handleCloseForm}
      />
    </div>
  );
}